import React, { useState, useEffect, useRef } from "react";
import { FaPen, FaTrash } from "react-icons/fa";
import styles from "./TablePagination.module.css";

const TablePagination = ({
  tabledata,
  initialRowsPerPage,
  imagecolumns,
  showEditOption,
  showDeleteOption,
  onClickEdit,
  onClickDel,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [isLastPage, setIsLastPage] = useState(false);
  const [totalPages, setTotalPages] = useState(
    Math.ceil(tabledata.length / rowsPerPage)
  );
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInputValue, setCustomInputValue] = useState("");
  const [showSlider, setShowSlider] = useState(false);
  const [sliderValue, setSliderValue] = useState(rowsPerPage);
  const [isDragging, setIsDragging] = useState(false);
  
  const sliderRef = useRef(null);
  const sliderTrackRef = useRef(null);
  
  const columns = tabledata.length > 0 ? Object.keys(tabledata[0]) : [];
  const imageColumnsExist =
    Array.isArray(imagecolumns) && imagecolumns.length > 0;

  // Smart predefined options based on data size
  const getPredefinedOptions = () => {
    const totalRecords = tabledata.length;
    const baseOptions = [5, 10, 25, 50, 100];
    
    if (totalRecords <= 100) {
      return [...baseOptions, totalRecords];
    } else if (totalRecords <= 500) {
      return [...baseOptions, 250, totalRecords];
    } else if (totalRecords <= 1000) {
      return [...baseOptions, 250, 500, totalRecords];
    } else {
      return [...baseOptions, 250, 500, 1000, totalRecords];
    }
  };

  // Get slider range based on data size
  const getSliderRange = () => {
    const totalRecords = tabledata.length;
    if (totalRecords <= 50) {
      return { min: 1, max: totalRecords, step: 1 };
    } else if (totalRecords <= 200) {
      return { min: 5, max: totalRecords, step: 5 };
    } else if (totalRecords <= 1000) {
      return { min: 10, max: totalRecords, step: 10 };
    } else {
      return { min: 25, max: Math.min(1000, totalRecords), step: 25 };
    }
  };

  // Load user preference from localStorage
  useEffect(() => {
    const savedRowsPerPage = localStorage.getItem('tableRowsPerPage');
    if (savedRowsPerPage) {
      const parsedValue = parseInt(savedRowsPerPage, 10);
      if (parsedValue <= tabledata.length) {
        setRowsPerPage(parsedValue);
        setSliderValue(parsedValue);
      }
    }
  }, [tabledata.length]);

  useEffect(() => {
    setIsLastPage(totalPages <= 1);
    setRowsPerPage(
      initialRowsPerPage > tabledata.length
        ? tabledata.length
        : initialRowsPerPage
    );
    setSliderValue(
      initialRowsPerPage > tabledata.length
        ? tabledata.length
        : initialRowsPerPage
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update slider value when rowsPerPage changes
  useEffect(() => {
    setSliderValue(rowsPerPage);
  }, [rowsPerPage]);

  function handleRowsPerPageChange(value) {
    const newRowsPerPage = parseInt(value, 10);
    if (newRowsPerPage > 0 && newRowsPerPage <= tabledata.length) {
      setRowsPerPage(newRowsPerPage);
      setSliderValue(newRowsPerPage);
      localStorage.setItem('tableRowsPerPage', newRowsPerPage.toString());
      const newTotalPages = Math.ceil(tabledata.length / newRowsPerPage);
      setTotalPages(newTotalPages);
      setCurrentPage(1);
      setIsFirstPage(true);
      setIsLastPage(newTotalPages <= 1);
      setShowCustomInput(false);
      setCustomInputValue("");
      setShowSlider(false);
    }
  }

  function handleQuickButtonClick(value) {
    handleRowsPerPageChange(value);
  }

  function handleCustomInputChange(e) {
    setCustomInputValue(e.target.value);
  }

  function handleCustomInputKeyPress(e) {
    if (e.key === 'Enter') {
      handleRowsPerPageChange(customInputValue);
    } else if (e.key === 'Escape') {
      setShowCustomInput(false);
      setCustomInputValue("");
    }
  }

  function handleCustomInputBlur() {
    if (customInputValue) {
      handleRowsPerPageChange(customInputValue);
    } else {
      setShowCustomInput(false);
    }
  }

  // Slider functions
  function handleSliderChange(e) {
    const value = parseInt(e.target.value, 10);
    setSliderValue(value);
  }

  function handleSliderMouseUp() {
    if (isDragging) {
      setIsDragging(false);
      handleRowsPerPageChange(sliderValue);
    }
  }

  function handleSliderKeyDown(e) {
    if (e.key === 'Enter') {
      handleRowsPerPageChange(sliderValue);
      setShowSlider(false);
    } else if (e.key === 'Escape') {
      setSliderValue(rowsPerPage);
      setShowSlider(false);
    }
  }

  function handleSliderBlur() {
    if (!isDragging) {
      handleRowsPerPageChange(sliderValue);
      setShowSlider(false);
    }
  }

  function handleFirstClick() {
    setCurrentPage(1);
    setIsFirstPage(true);
    setIsLastPage(false);
  }

  function handlePreviousClick() {
    const newCurrentPage = currentPage - 1;
    setCurrentPage(newCurrentPage);
    setIsFirstPage(newCurrentPage === 1);
    setIsLastPage(newCurrentPage === totalPages);
  }

  function handleNextClick() {
    const newCurrentPage = currentPage + 1;
    setCurrentPage(newCurrentPage);
    setIsFirstPage(newCurrentPage === 1);
    setIsLastPage(newCurrentPage === totalPages);
  }

  function handleLastClick() {
    setCurrentPage(totalPages);
    setIsFirstPage(false);
    setIsLastPage(true);
  }

  function handleEditClick(rows) {
    if (rows.is_default !== 1) {
      onClickEdit(rows.id);
    }
  }

  function handleDelClick(rows) {
    // console.log("Delete ID:", rows.id);
    if (rows.is_default !== 1) {
      onClickDel(rows.id);
    }
  }

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Number(startIndex) + Number(rowsPerPage);
  const currentPageData = tabledata.slice(startIndex, endIndex);

  const predefinedOptions = getPredefinedOptions();
  const sliderRange = getSliderRange();
  const previewPages = Math.ceil(tabledata.length / sliderValue);

  return (
    <div className="table-responsive m-3">
      <table className="table table-striped table-info text-center">
        <thead>
          <tr>
            <th>No</th>
            {columns.map((column, index) => {
              if (column !== "id") {
                return <th key={index}>{column.toUpperCase()}</th>;
              }
              return null;
            })}
            {(showEditOption || showDeleteOption) && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {currentPageData.map((rows, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              {columns.map((column, index1) => {
                if (column !== "id") {
                  return (
                    <td key={index1}>
                      {imageColumnsExist &&
                      imagecolumns.includes(column) &&
                      rows[column] !== "" ? (
                        <img
                          src={"/images/" + rows[column]}
                          alt="sampleimg"
                          width="50"
                          height="50"
                        />
                      ) : (
                        rows[column]
                      )}
                    </td>
                  );
                }
                return null;
              })}
              {(showEditOption || showDeleteOption) && (
                <td>
                  {showEditOption && rows.is_default !== 1 && (
                    <FaPen
                      className={`${styles.clickable} text-primary m-1`}
                      onClick={() => handleEditClick(rows)}
                    />
                  )}
                  {showDeleteOption && rows.is_default !== 1 && (
                    <FaTrash
                      className={`${styles.clickable} text-danger m-1`}
                      onClick={() => handleDelClick(rows)}
                    />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Enhanced Rows Per Page Controls */}
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <div className={`d-flex align-items-center flex-wrap mb-2 ${styles.rowsPerPageContainer}`}>
          <span className="me-2 fw-bold">Rows per page:</span>
          
          {/* Smart Dropdown */}
          <select
            className="form-select me-2"
            style={{ width: 'auto', minWidth: '80px' }}
            value={rowsPerPage}
            onChange={(e) => handleRowsPerPageChange(e.target.value)}
          >
            {predefinedOptions.map((option) => (
              <option key={option} value={option}>
                {option === tabledata.length ? 'All' : option}
              </option>
            ))}
          </select>

          {/* Quick Action Buttons */}
          <div className="d-flex me-2">
            {[10, 25, 50, 100].map((value) => (
              <button
                key={value}
                className={`btn btn-sm me-1 ${styles.quickButton} ${rowsPerPage === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleQuickButtonClick(value)}
                disabled={value > tabledata.length}
                title={`Show ${value} rows per page`}
              >
                {value}
              </button>
            ))}
            {tabledata.length > 100 && (
              <button
                className={`btn btn-sm me-1 ${styles.quickButton} ${rowsPerPage === tabledata.length ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleQuickButtonClick(tabledata.length)}
                title="Show all rows"
              >
                All
              </button>
            )}
          </div>

          {/* Range Slider */}
          {!showSlider ? (
            <button
              className={`btn btn-sm btn-outline-warning ${styles.quickButton}`}
              onClick={() => setShowSlider(true)}
              title="Use slider to select rows per page"
            >
              Slider
            </button>
          ) : (
            <div className={`d-flex align-items-center ${styles.sliderContainer}`}>
              <div className="d-flex align-items-center me-2">
                <span className="small text-muted me-1">{sliderRange.min}</span>
                <input
                  ref={sliderRef}
                  type="range"
                  className={`form-range ${styles.rangeSlider}`}
                  min={sliderRange.min}
                  max={sliderRange.max}
                  step={sliderRange.step}
                  value={sliderValue}
                  onChange={handleSliderChange}
                  onMouseDown={() => setIsDragging(true)}
                  onMouseUp={handleSliderMouseUp}
                  onKeyDown={handleSliderKeyDown}
                  onBlur={handleSliderBlur}
                  style={{ width: '120px' }}
                  title="Drag to adjust rows per page"
                />
                <span className="small text-muted ms-1">{sliderRange.max}</span>
              </div>
              <div className="d-flex align-items-center me-2">
                <span className="badge bg-info me-1">{sliderValue}</span>
                <span className="small text-muted">({previewPages} pages)</span>
              </div>
              <button
                className={`btn btn-sm btn-success me-1 ${styles.actionButton}`}
                onClick={() => handleRowsPerPageChange(sliderValue)}
                title="Apply slider value"
              >
                ✓
              </button>
              <button
                className={`btn btn-sm btn-secondary ${styles.actionButton}`}
                onClick={() => {
                  setSliderValue(rowsPerPage);
                  setShowSlider(false);
                }}
                title="Cancel slider"
              >
                ✕
              </button>
            </div>
          )}

          {/* Custom Input */}
          {!showCustomInput ? (
            <button
              className={`btn btn-sm btn-outline-info ${styles.quickButton}`}
              onClick={() => setShowCustomInput(true)}
              title="Enter custom number of rows"
            >
              Custom
            </button>
          ) : (
            <div className={`d-flex align-items-center ${styles.customInputContainer}`}>
              <input
                type="number"
                className={`form-control form-control-sm me-1 ${styles.customInput}`}
                style={{ width: '80px' }}
                value={customInputValue}
                onChange={handleCustomInputChange}
                onKeyPress={handleCustomInputKeyPress}
                onBlur={handleCustomInputBlur}
                placeholder="Enter"
                min="1"
                max={tabledata.length}
                autoFocus
              />
              <button
                className={`btn btn-sm btn-success me-1 ${styles.actionButton}`}
                onClick={() => handleRowsPerPageChange(customInputValue)}
              >
                ✓
              </button>
              <button
                className={`btn btn-sm btn-secondary ${styles.actionButton}`}
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomInputValue("");
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Page Info */}
        <div className={`text-muted small mb-2 ${styles.pageInfo}`}>
          Showing {startIndex + 1}-{Math.min(endIndex, tabledata.length)} of {tabledata.length} entries
        </div>

        {/* Pagination Controls */}
        <div className={`d-flex ${styles.paginationContainer}`}>
          <button
            className={`btn btn-outline-secondary btn-sm mr-2 ${styles.actionButton}`}
            onClick={handleFirstClick}
            disabled={isFirstPage}
            title="Go to first page"
          >
            First
          </button>
          <button
            className={`btn btn-outline-secondary btn-sm mr-2 ${styles.actionButton}`}
            onClick={handlePreviousClick}
            disabled={isFirstPage}
            title="Go to previous page"
          >
            Previous
          </button>
          <span className="d-flex align-items-center mx-2">
            <span className={`badge ${styles.pageBadge}`}>
              Page {currentPage} of {totalPages}
            </span>
          </span>
          <button
            className={`btn btn-outline-secondary btn-sm mr-2 ${styles.actionButton}`}
            onClick={handleNextClick}
            disabled={isLastPage}
            title="Go to next page"
          >
            Next
          </button>
          <button
            className={`btn btn-outline-secondary btn-sm ${styles.actionButton}`}
            onClick={handleLastClick}
            disabled={isLastPage}
            title="Go to last page"
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
